"use client";
import { useEffect, useCallback } from "react";
import { Package } from "lucide-react";
import { toast } from "sonner";
import useShippingTracking from "@/app/lib/features/shippingTracking/shippingTracking.hook";
import { ShippingTrackingData } from "@/app/schemas/shippingTrackingSchema";
import ShippingTrackingForm from "@/app/components/common/shipping-tracking/ShippingTrackingForm";
import ShippingTrackingResult from "@/app/components/common/shipping-tracking/ShippingTrackingResult";
import ShippingTrackingResultSkeleton from "@/app/components/elements/skeletons/ShippingTrackingResultSkeleton";
import { trackGeneric } from "@/app/lib/tracking/interactions";

const ShippingTrackingClient = () => {
  const {
    metadata,
    isMetadataLoading,
    trackingResult,
    isResultLoading,
    isResultError,
    resultError,
    fetchMetadata,
    submitTrackOrder,
  } = useShippingTracking();

  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  const handleSubmit = useCallback(
    async (data: ShippingTrackingData) => {
      trackGeneric("shipping_track", "click");
      const success = await submitTrackOrder({
        orderNumber: data.orderNumber,
        phone_number: data.phoneNumber,
      });
      if (!success) {
        toast.error(resultError ?? "Kargo bilgisi bulunamadı.");
      }
    },
    [submitTrackOrder, resultError],
  );

  return (
    <div className="flex-1 w-full lg:w-auto py-6 md:py-8">
      <div className="max-w-2xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
            <Package className="w-10 h-10 text-gray-600" />
          </div>
          {isMetadataLoading && !metadata ? (
            <div className="space-y-2">
              <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mx-auto" />
              <div className="h-4 w-72 bg-gray-100 rounded animate-pulse mx-auto" />
            </div>
          ) : metadata ? (
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                {metadata.title}
              </h1>
              <p className="text-lg text-gray-600 max-w-lg mx-auto">
                {metadata.subtitle}
              </p>
            </div>
          ) : (
            // Başlık API'den geliyor ve fetch client'ta yapılıyor; metadata yokken
            // sayfa h1'siz kalıyordu (SSR HTML'inde de). Sayfanın kendi başlığı
            // fallback: metadata gelince yerini API metnine bırakır.
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
              Kargo Takibi
            </h1>
          )}
        </div>

        {metadata && (
          <ShippingTrackingForm
            metadata={metadata}
            isLoading={isResultLoading}
            onSubmit={handleSubmit}
          />
        )}

        {isResultLoading && <ShippingTrackingResultSkeleton />}
        {!isResultLoading && trackingResult && (
          <ShippingTrackingResult result={trackingResult} />
        )}
        {isResultError && !isResultLoading && (
          <p className="text-center text-sm text-red-500">{resultError}</p>
        )}

        {metadata && metadata.supports.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {metadata.supports.map((support, index) => (
              <div
                key={index}
                className="bg-gray-50 border border-gray-200 rounded-lg p-6"
              >
                <h3 className="font-semibold text-gray-900 mb-2">
                  {support.icon} {support.title}
                </h3>
                <p className="text-sm text-gray-700">{support.subtitle}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShippingTrackingClient;